import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import Alert from "@mui/material/Alert";

const PinEntryPage = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const clubId = searchParams.get("clubId");
  const doorId = searchParams.get("doorId");

  const [pins, setPins] = useState(["", "", "", ""]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [clubName, setClubName] = useState("");
  const [doorName, setDoorName] = useState("");
  const [isPlaytomic, setIsPlaytomic] = useState(false);
  const [phone, setPhone] = useState("");
  const [success, setSuccess] = useState("");
  const inputRefs = useRef([]);

  useEffect(() => {
    const fetchClubName = async () => {
      try {
        const response = await axios.get(`/api/club-name?clubId=${clubId}`);
        setClubName(response.data);
      } catch (error) {
        console.error("Error fetching club name:", error);
        setError("Club not found.");
      }
    };

    const fetchClubType = async () => {
      try {
        const response = await axios.get(`/api/club-type?clubId=${clubId}`);
        setIsPlaytomic(response.data.integrated_system === "playtomic");
      } catch (error) {
        console.error("Error fetching club type:", error);
      }
    };

    const fetchDoorName = async () => {
      try {
        const response = await axios.get(`/api/door-name?doorId=${doorId}`);
        if (response.data) {
          setDoorName(response.data);
        }
      } catch (error) {
        console.error("Error fetching door name:", error);
      }
    };

    if (clubId) {
      fetchClubName();
      fetchClubType();
    }
    if (doorId) {
      fetchDoorName();
    }
  }, [clubId, doorId]);

  const handleKeyDown = (e, index) => {
    const { key } = e;

    // Handle backspace navigation
    if (key === "Backspace") {
      if (pins[index] === "" && index > 0) {
        const newPins = [...pins];
        newPins[index - 1] = "";
        setPins(newPins);
        inputRefs.current[index - 1]?.focus();
      }
      return;
    }
  };

  const handlePinChange = (e, index) => {
    const digit = e.target.value.replace(/\D/g, "").slice(-1);
    const newPins = [...pins];
    newPins[index] = digit;
    setPins(newPins);

    if (digit && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    const pin = pins.join("");

    if (pin.length !== 4 || !/^\d+$/.test(pin)) {
      setError("Please enter a valid 4-digit PIN.");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `/api/doors/pin-validate?clubId=${clubId}&doorId=${doorId}`,
        { pin }
      );

      if (response.data.success) {
        setSuccess("PIN verified successfully!");
        setPins(["", "", "", ""]);
      } else {
        setError("Invalid PIN. Please try again.");
        setPins(["", "", "", ""]);
      }
    } catch (error) {
      console.error("Error:", error);
      if (error.response) {
        if (error.response.status === 400) {
          setError(
            "PIN validation failed. Please check your PIN and try again."
          );
          setPins(["", "", "", ""]);
        } else if (error.response.status === 500) {
          setError("An error occurred on the server. Please try again later.");
          setPins(["", "", "", ""]);
        } else {
          setError("An unexpected error occurred. Please try again later.");
          setPins(["", "", "", ""]);
        }
      } else if (error.request) {
        setError(
          "No response received from the server. Please try again later."
        );
        setPins(["", "", "", ""]);
      } else {
        setError("An error occurred. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setError("");
    setSuccess("");
    setPins(["", "", "", ""]);
    setPhone("");
  };

  const handlePhoneSubmit = async () => {
    setError("");
    setSuccess("");

    if (!/^[0-9]{9}$/.test(phone)) {
      setError("Please enter a valid 9-digit Spanish phone number.");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `/api/doors/phone-validate?clubId=${clubId}&doorId=${doorId}`,
        { phone }
      );

      if (response.data.success) {
        setSuccess("Phone access granted!");
        setPhone("");
      } else {
        setError("Phone access denied. Please try again.");
      }
    } catch (error) {
      console.error("Error:", error);
      if (error.response) {
        if (error.response.status === 400) {
          if (error.response.data.error) {
            setError(error.response.data.error);
          } else {
            setError(
              "Phone validation failed. Please check your number and try again."
            );
          }
        } else if (error.response.status === 500) {
          setError("An error occurred on the server. Please try again later.");
        } else {
          setError("An unexpected error occurred. Please try again later.");
        }
      } else if (error.request) {
        setError(
          "No response received from the server. Please try again later."
        );
      } else {
        setError("An error occurred. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  // If clubId or doorId is not provided, show a 404 error message
  if (!clubId || !doorId) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gray-100">
        <Alert severity="error">Error 404: Page Not Found</Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col justify-start items-center bg-gray-100 pt-10">
      {error && (
        <div className="mb-4 text-center">
          <Alert severity="error">{error}</Alert>
          <div className="flex justify-center mt-2">
            <button
              onClick={handleRetry}
              className="bg-gray-500 hover:bg-gray-700 text-white py-2 px-4 rounded"
            >
              Reintentar
            </button>
          </div>
        </div>
      )}
      {success && <Alert severity="success">{success}</Alert>}
      {!error && !success && (
        <div className="text-center mb-8 shadow p-10 bg-white">
          <h2 className="text-3xl font-bold mb-6">Ingresa tu PIN</h2>
          <div className="flex flex-col items-center mb-4">
            <p className="text-xl text-gray-800 font-semibold mb-2">
              Nombre del club: {clubName}
            </p>
            <p className="text-xl text-gray-800 font-semibold mb-2">
              Puerta: {doorName || doorId}
            </p>
          </div>

          {isPlaytomic && (
            <div className="flex flex-col items-center mb-6">
              <label className="text-xl text-gray-800 font-semibold mb-2">
                Ingresa el número de teléfono (sin prefijo):
              </label>
              <input
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                className="w-56 h-10 text-center border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-blue-500"
                placeholder="600123456"
                maxLength={9}
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 9))}
              />
              <button
                onClick={handlePhoneSubmit}
                className="mt-3 bg-blue-500 hover:bg-blue-700 text-white py-2 px-4 rounded"
                disabled={loading}
              >
                {loading ? "Submitting..." : "Submit"}
              </button>
            </div>
          )}

          <div className="flex justify-center items-center mb-8">
            {pins.map((pin, index) => (
              <input
                key={index}
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                className="w-12 h-12 text-center border border-gray-300 rounded-md mx-1 focus:outline-none focus:ring focus:ring-blue-500"
                maxLength="1"
                value={pin}
                onChange={(e) => handlePinChange(e, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                ref={(el) => (inputRefs.current[index] = el)}
              />
            ))}
          </div>

          <button
            onClick={handleSubmit}
            className="bg-blue-500 hover:bg-blue-700 text-white py-2 px-4 rounded"
            disabled={loading}
          >
            {loading ? "Submitting..." : "Submit"}
          </button>
        </div>

      )}
    </div>
  );
};

export default PinEntryPage;
